import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Wraps any route that requires authentication.
 *
 * Behaviour:
 *  - Loading  → spinner (prevents flash of wrong content)
 *  - No user  → redirect to /login (preserves intended destination)
 *  - Needs consent → redirect to /auth/consent
 *  - All good → render children
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, needsConsent } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
          <p className="font-body text-sm text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsConsent) {
    return <Navigate to="/auth/consent" replace />;
  }

  return <>{children}</>;
}
